import { VoicemailUsage } from '@weave/schema-gen-ts/dist/schemas/phone/voicemail-config/v2/voicemail_config_service.pb';
import { useQuery } from 'react-query';
import { VoicemailConfigAPI } from '@frontend/api-voicemail-boxes';
import { Trans, useTranslation } from '@frontend/i18n';
import { VoicemailSchemaService } from '@frontend/schema';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { ContentLoader, Modal, ModalControlModalProps, Text, useAlert, useModalControl } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { trackingId } from '../../../tracking';
import { DeleteFlow } from '../../common/delete-flow';
import { VoicemailBoxReadResponse } from './types';
import { useVoicemailBoxMutations } from './use-voicemail-box-mutations';

export const DeleteVoicemailBoxButton = ({
  box,
  phoneTenantId,
  disabled,
}: {
  box: VoicemailBoxReadResponse;
  phoneTenantId: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');

  const { modalProps, triggerProps } = useModalControl();

  return (
    <DeleteFlow.Button
      label={t('Delete Voicemail Box')}
      trackingId={trackingId({ context: 'setting', feature: 'voicemail-boxes', details: 'delete' })}
      triggerProps={triggerProps}
      disabled={disabled}
      disabledTooltip={t(`Voicemail Boxes belonging to a device can't be deleted.`)}
    >
      <VoicemailBoxDeleteModal
        modalProps={modalProps}
        name={box.mailbox.name}
        id={box.mailbox.id}
        phoneTenantId={phoneTenantId}
      />
    </DeleteFlow.Button>
  );
};

export const DeleteVoicemailBoxIconButton = ({
  name,
  id,
  phoneTenantId,
  disabled,
}: {
  name: string;
  id: string;
  phoneTenantId: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();

  return (
    <DeleteFlow.IconButton
      label={disabled ? t('Voicemail Boxes belonging to a device cannot be deleted.') : t('Delete Voicemail Box.')}
      triggerProps={triggerProps}
      disabled={disabled}
      trackingId={trackingId({ context: 'setting', feature: 'voicemail-boxes', details: 'index-delete' })}
    >
      <VoicemailBoxDeleteModal modalProps={modalProps} name={name} id={id} phoneTenantId={phoneTenantId} />
    </DeleteFlow.IconButton>
  );
};

const VoicemailBoxDeleteModal = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: queryKeys.settings.listVoicemailBoxUsageV2(id),
    queryFn: () => VoicemailConfigAPI.UsageV2({ voicemailBoxId: id }),
    enabled: modalProps.show,
    staleTime: 0,
  });

  return (
    <DeleteFlow.Modal
      CanDeleteContent={VoicemailBoxCanDeleteContent}
      CannotDeleteContent={VoicemailBoxCannotDeleteContentV2}
      id={id}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      modalProps={modalProps}
      name={name}
      phoneTenantId={phoneTenantId}
      usage={data?.voicemailUsages || []}
    />
  );
};

const VoicemailBoxCanDeleteContent = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { remove } = useVoicemailBoxMutations(phoneTenantId);
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { navigate } = useSettingsNavigate();
  const { data, isLoading } = useQuery({
    queryKey: [phoneTenantId, ...queryKeys.settings.countVoicemails(id)],
    queryFn: () => VoicemailSchemaService.CountVoicemails({ mailboxId: id }),
    enabled: modalProps.show,
    staleTime: 0,
  });

  const removeFn = () =>
    remove.mutate(
      { voicemailBoxId: id },
      {
        onSuccess: () => {
          alerts.success(t('Voicemail Box deleted successfully.'));
          modalProps.onClose();
          navigate({ to: '/phone/voicemail-box' });
        },
        onError: () => {
          alerts.error(t('System failed to delete Voicemail Box. Please try again.'));
        },
      }
    );

  if (isLoading) {
    return (
      <Modal.Body>
        <div style={{ height: 100, width: 200 }}>
          <ContentLoader show message={t('Checking for voicemails')} />
        </div>
      </Modal.Body>
    );
  }

  const count = data?.total || 0;

  return (
    <DeleteFlow.CanDeleteContent
      modalProps={modalProps}
      name={name}
      title={t('Delete Voicemail Box?')}
      removeFn={removeFn}
      body={
        <Trans t={t} name={name} count={count}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <Text>
              There are currently{' '}
              <Text as='span' weight='semibold'>
                {{ count }} voicemails
              </Text>{' '}
              in{' '}
              <Text as='span' weight='semibold'>
                {{ name }}
              </Text>
              .
            </Text>
            <Text>Are you sure you want to delete this Voicemail Box? This action cannot be undone.</Text>
          </div>
        </Trans>
      }
    />
  );
};

const VoicemailBoxCannotDeleteContentV2 = ({
  id,
  modalProps,
  name,
  usage,
  disableLinks = false,
}: {
  id: string;
  modalProps: ModalControlModalProps;
  name: string;
  usage: VoicemailUsage[];
  disableLinks?: boolean;
}) => {
  const { t } = useTranslation('phone');

  const usageCount = usage.length;

  return (
    <DeleteFlow.CannotDeleteContentV2
      id={id}
      showTable={true}
      modalProps={modalProps}
      disableLinks={disableLinks}
      usages={usage}
      title={t('Cannot Delete Voicemail Box')}
      body={
        <Trans t={t} name={name}>
          <Text>
            <Text as='span' weight='semibold'>
              {name}
            </Text>{' '}
            {usageCount === 1
              ? t(
                  'can’t be deleted because it is currently in use. The Voicemail Box must be removed from the following place in order to be deleted.'
                )
              : t(
                  'can’t be deleted because it is currently in use. The Voicemail Box must be removed from the following places in order to be deleted.'
                )}
          </Text>
        </Trans>
      }
    />
  );
};
